// Consent-gated analytics.
//
// GDPR/PECR fix: Google Analytics, Vercel Analytics and Speed Insights all set
// non-essential cookies / collect device data, so none of them may load until
// the visitor has actively accepted cookies. This component renders nothing and
// loads nothing until getConsent() === 'accepted', and reacts live to the
// banner so accepting starts analytics immediately (no reload needed).

import { useEffect, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { getConsent, subscribeConsent, type ConsentValue } from './consent';

// GA4 measurement ID (previously hard-coded in index.html where it fired on load).
const GA_MEASUREMENT_ID = 'G-G24J3BKZQH';

// Module-level guard so GA is injected at most once, even if this component
// remounts (e.g. React StrictMode double-invoke in dev).
let gaLoaded = false;

// Dynamically inject the gtag.js script and initialise GA. Only ever called
// after consent === 'accepted'.
function loadGoogleAnalytics(): void {
  if (gaLoaded || typeof window === 'undefined') return;
  gaLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  const w = window as unknown as {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  // Canonical gtag shim — pushes the raw `arguments` object, as GA expects.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  }
  w.gtag = gtag as (...args: unknown[]) => void;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID);
}

export default function ConsentedAnalytics() {
  const [consent, setConsentState] = useState<ConsentValue | null>(() =>
    getConsent()
  );

  // Keep in sync with the cookie banner (and any other tab writing consent).
  useEffect(() => subscribeConsent(setConsentState), []);

  // Load GA the moment consent is granted (covers both returning visitors who
  // already accepted and a fresh "Accept All" click).
  useEffect(() => {
    if (consent === 'accepted') loadGoogleAnalytics();
  }, [consent]);

  // No consent → render nothing, so Vercel Analytics/Speed Insights never mount.
  if (consent !== 'accepted') return null;

  return (
    <>
      <Analytics />
      <SpeedInsights />
    </>
  );
}
